import OnvoBase from "../base.js";
import { Team } from "../types/index.js";

// Team endpoints
export class OnvoTeams extends OnvoBase {
  /**
   * Retrieves a list of all teams.
   * @returns {Promise<Team[]>} A promise that resolves to an array of Team objects.
   */
  list(): Promise<Team[]> {
    return this._fetchBase("/api/teams") as Promise<Team[]>;
  }

  /**
   * Retrieves a specific team by ID.
   * @param {string} id - The ID of the team to retrieve.
   * @returns {Promise<Team>} A promise that resolves to the Team object.
   */
  get(id: string): Promise<Team> {
    return this._fetchBase("/api/teams/" + id) as Promise<Team>;
  }

  /**
   * Updates a specific team.
   * @param {string} id - The ID of the team to update.
   * @param {Partial<Team>} body - The updated team data.
   * @returns {Promise<Team>} A promise that resolves to the updated Team object.
   */
  update(id: string, body: Partial<Team>): Promise<Team> {
    return this._fetchBase("/api/teams/" + id, "PUT", body) as Promise<Team>;
  }

  /**
   * Creates a new team and associates it with an account.
   * @param {string} accountId - The ID of the account to associate with the team.
   * @param {Partial<Team>} teamData - The data for the new team.
   * @returns {Promise<Team>} A promise that resolves to the created Team object.
   * @throws Will throw an error if the request fails.
   */
  async create(accountId: string, teamData: Partial<Team>): Promise<Team> {
    try {
      const team = await this._fetchBase(
        "/api/teams",
        "POST",
        teamData
      ) as Team;

      await this._fetchBase(
        `/api/accounts/${accountId}/update-team`,
        "POST",
        { teamId: team.id }
      );

      return team;
    } catch (e: any) {
      console.error('Error creating team:', e);
      throw new Error(e.message || 'Failed to create team');
    }
  }
}
